import {
  createContext,
  useContext,
  type AnchorHTMLAttributes,
  type ElementType,
  type ForwardRefExoticComponent,
  type ReactNode,
  type RefAttributes,
} from 'react';

/** Props an injected link component must accept. Mirrors a native `<a>`. */
export interface LinkComponentProps extends Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> {
  /** Unresolved destination; in-app hrefs are root-relative (`/foo`). */
  href: string;
}

/** An app-supplied link component. Must forward its ref to the anchor. */
export type LinkComponent = ForwardRefExoticComponent<
  LinkComponentProps & RefAttributes<HTMLAnchorElement>
>;

const LinkComponentContext = createContext<LinkComponent | undefined>(
  undefined,
);

/**
 * Supplies the component EDS renders in-app links with, keeping `packages/eds`
 * router-agnostic (§1 Platform Agnosticism).
 *
 * Injecting a component rather than a navigate callback is what keeps the
 * rendered `href` and the navigation in agreement: the router computes both
 * through the same code path, so a base path can't apply to one and not the
 * other. Without a provider, links fall back to a native `<a>`.
 */
export function LinkNavigationProvider({
  linkComponent,
  children,
}: {
  linkComponent: LinkComponent;
  children: ReactNode;
}) {
  return (
    <LinkComponentContext.Provider value={linkComponent}>
      {children}
    </LinkComponentContext.Provider>
  );
}

/** The injected link component, or `undefined` when no provider is mounted. */
export function useLinkComponent(): LinkComponent | undefined {
  return useContext(LinkComponentContext);
}

/**
 * Whether `href` points at an in-app route. In-app routes are root-relative
 * paths (`/foo`); anything with a scheme (`https:`, `mailto:`, `tel:`),
 * protocol-relative (`//host`), or a bare hash/relative link is left to the
 * browser.
 */
export function isInAppHref(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

/**
 * The element to render `href` with: the injected link component for same-tab
 * in-app hrefs, a native `<a>` for everything else. Callers pass the unresolved
 * `href` straight through — resolving it is the injected component's job.
 */
export function useLinkElement(
  href: string | undefined,
  target?: string,
): LinkComponent | ElementType<AnchorHTMLAttributes<HTMLAnchorElement>> {
  const LinkComponent = useLinkComponent();

  const isSameTab = target === undefined || target === '' || target === '_self';
  const isRoutable =
    href !== undefined && isInAppHref(href) && isSameTab && !!LinkComponent;

  return isRoutable ? LinkComponent : 'a';
}
